import React, { useEffect, useState } from 'react';
import { useNavigate, NavigateFunction } from 'react-router-dom';
import { Session } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { getSupabaseClient } from '../services/supabase';
import { signupUser } from '../services/credits';
import { invalidateAfterSignup } from '../lib/invalidation';
import { OAUTH_RETURN_KEY } from '../utils/constants';
import { markSignupRecoveryDone } from '../utils/signupRecovery';
import { deriveCohort } from '../utils/cohort';
import { usePostHog } from 'posthog-js/react';
import { trackAuthEvent } from '../utils/trackAuthEvent';

// ============================================================================
// Types
// ============================================================================

interface CallbackContext {
  startTime: number;
  isMobile: boolean;
  isFacebookBrowser: boolean;
  referrer: string;
  wasOnCriticalPage: boolean;
  pageType: 'upgrade' | 'credits' | 'checkout' | null;
  hadPendingOAuth: boolean;
  returnPath: string;
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Gather all context needed for the callback flow:
 * timing, device info, referrer analysis, and stored return path.
 */
function getCallbackContext(): CallbackContext {
  const startTime = Date.now();
  const isMobile = /Android|iPhone|iPad/i.test(navigator.userAgent);
  const isFacebookBrowser = /FBAN|FBAV/i.test(navigator.userAgent);

  const referrer = document.referrer;
  const wasOnUpgradePage = referrer.includes('/upgrade');
  const wasOnCreditsPage = referrer.includes('/credits');
  const wasOnCheckoutPage = referrer.includes('checkout');
  const wasOnCriticalPage = wasOnUpgradePage || wasOnCreditsPage || wasOnCheckoutPage;
  const pageType = wasOnUpgradePage ? 'upgrade' : wasOnCreditsPage ? 'credits' : wasOnCheckoutPage ? 'checkout' : null;

  // Read the return path (cleared AFTER redirect to avoid race with App.tsx UNIFIED GUARD)
  const storedReturnPath = localStorage.getItem(OAUTH_RETURN_KEY);
  // Fallback: check lander3 OAuth return key if primary key is missing (mobile sessionStorage loss)
  const lander3ReturnPath = localStorage.getItem('lander3_oauth_return');
  const hadPendingOAuth = !!(storedReturnPath || lander3ReturnPath);
  const returnPath = storedReturnPath || lander3ReturnPath || '/';

  return {
    startTime,
    isMobile,
    isFacebookBrowser,
    referrer,
    wasOnCriticalPage,
    pageType,
    hadPendingOAuth,
    returnPath,
  };
}

/**
 * Track unexpected OAuth redirect from revenue-critical pages.
 */
function trackRevenueProtection(
  posthog: ReturnType<typeof usePostHog>,
  context: CallbackContext
): void {
  trackAuthEvent(posthog, 'unexpected_oauth_redirect', {
    from_page: context.referrer,
    is_critical_page: true,
    page_type: context.pageType,
    user_agent: navigator.userAgent,
    is_mobile: context.isMobile,
    timestamp: new Date().toISOString(),
  }, null);
}

/**
 * Check URL query string and hash for OAuth/email errors and handle navigation.
 * Returns true if an error was found and handled (caller should return early).
 *
 * Facebook's in-app browser replays stale OAuth callback URLs on page
 * interactions (e.g. clicking play on results video). These arrive with
 * error params in the query string (?error=invalid_request&error_code=
 * bad_oauth_state), not the hash fragment. We check both locations.
 */
function checkForOAuthErrors(navigate: NavigateFunction): boolean {
  // 1. Check query string (FB in-app browser stale replays)
  const queryParams = new URLSearchParams(window.location.search);
  const queryErrorCode = queryParams.get('error_code');

  if (queryParams.has('error') || queryErrorCode) {
    if (queryErrorCode === 'bad_oauth_state') {
      // Stale OAuth replay (common in FB in-app browser). Use history.back()
      // to restore the previous page from bfcache with full React state intact
      // (e.g. i2v results modal stays open). Falls back to savedPath if no history.
      if (window.history.length > 1) {
        window.history.back();
      } else {
        const savedPath = sessionStorage.getItem('ga:lastPath') || '/';
        window.location.replace(savedPath);
      }
      return true;
    }

    window.history.replaceState(null, '', window.location.pathname);
    const description = queryParams.get('error_description');
    navigate('/login?error=auth_failed&message=' + encodeURIComponent(description || 'Authentication failed. Please try signing in again.'), { replace: true });
    return true;
  }

  // 2. Check hash fragment (standard Supabase error delivery)
  const hash = window.location.hash;
  if (!hash.includes('error=')) {
    return false;
  }

  const errorParams = new URLSearchParams(hash.substring(1));
  const errorCode = errorParams.get('error_code');
  const errorDescription = errorParams.get('error_description');

  if (errorCode === 'bad_oauth_state') {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      const savedPath = sessionStorage.getItem('ga:lastPath') || '/';
      window.location.replace(savedPath);
    }
    return true;
  }

  window.history.replaceState(null, '', window.location.pathname);
  navigate('/login?error=email_expired&message=' + encodeURIComponent(errorDescription || 'Email link is invalid or has expired. Please request a new confirmation email.'), { replace: true });
  return true;
}

/**
 * Get the current session from Supabase, rejecting stale callbacks.
 * Returns null if no valid session (caller should return early).
 */
async function getSessionOrReject(
  context: CallbackContext,
  navigate: NavigateFunction
): Promise<Session | null> {
  const supabase = getSupabaseClient();
  const { data: { session } } = await supabase.auth.getSession();

  // Reject genuinely stale callbacks (e.g. address-bar nav to /auth/callback
  // long after auth). Detect via session.user.last_sign_in_at freshness
  // rather than localStorage, because cross-context OAuth handoff (Android
  // Custom Tab → WebView) loses the OAUTH_RETURN_KEY between origins —
  // hadPendingOAuth=false there is a false negative, not a stale signal.
  if (session) {
    const lastSignInMs = session.user.last_sign_in_at
      ? Date.parse(session.user.last_sign_in_at)
      : 0;
    const isFreshSignIn = Date.now() - lastSignInMs < 120_000;
    if (!isFreshSignIn) {
      window.history.replaceState(null, '', window.location.pathname);
      const savedPath = sessionStorage.getItem('ga:lastPath') || '/';
      navigate(savedPath, { replace: true });
      return null;
    }
  }

  if (!session) {
    navigate('/login', { replace: true });
    return null;
  }

  return session;
}

/**
 * Extract email from session, falling back to user_metadata if needed.
 * Returns null if email unavailable (caller should return early).
 */
async function resolveEmail(
  session: Session,
  navigate: NavigateFunction,
  setStatus: (status: string) => void
): Promise<string | null> {
  let userEmail: string = session.user.email || '';

  if (!userEmail) {
    const emailFromMetadata = session.user.user_metadata?.email;
    if (!emailFromMetadata) {
      setStatus('Error: Email not provided by OAuth provider');
      await new Promise((resolve) => setTimeout(resolve, 3000));
      navigate('/?error=no_email', { replace: true });
      return null;
    }
    userEmail = emailFromMetadata;
    session.user.email = emailFromMetadata;
  }

  return userEmail;
}

/**
 * Call the signup API to create/get user and grant signup bonus.
 * Returns whether this is a new user (for analytics).
 */
async function handleSignup(
  email: string,
  session: Session,
  posthog: ReturnType<typeof usePostHog>,
  setStatus: (status: string) => void
): Promise<boolean> {
  setStatus('Setting up your account...');

  // Raw posthog.capture intentional (bypasses useTracking's 10s dedup) so
  // every grant attempt is recorded. ms_since_otp_sent is omitted — this is
  // the OAuth path, no email round-trip. supabase_user_id lives in the base
  // bag because session is a parameter (always in scope here).
  const grantProps = (extra: Record<string, any> = {}) => ({
    provider: 'oauth_google',
    supabase_user_id: session.user.id,
    cohort: deriveCohort(navigator.userAgent),
    release: process.env.REACT_APP_RELEASE ?? 'unknown',
    ms_since_page_load: Math.round(performance.now()),
    online: navigator.onLine,
    visibility: document.visibilityState,
    email_domain: email.split('@')[1]?.toLowerCase(),
    ...extra,
  });
  const grantOutcome = (outcome: string, extra: Record<string, any> = {}) =>
    posthog.capture('auth_grant_outcome', { outcome, ...grantProps(extra) });

  posthog.capture('auth_grant_started', grantProps());

  Sentry.addBreadcrumb({
    category: 'auth-handoff',
    message: 'signupUser_started',
    level: 'info',
    data: { path: 'auth_callback' },
  });

  let isNewUser = false;
  try {
    const signupResult = await signupUser(
      email,
      session.user.id,
      session.user.user_metadata?.full_name
    );

    Sentry.addBreadcrumb({
      category: 'auth-handoff',
      message: 'signupUser_returned',
      level: 'info',
      data: {
        path: 'auth_callback',
        signup_bonus_granted: signupResult.signup_bonus_granted,
        credits: signupResult.credits,
      },
    });

    grantOutcome(
      signupResult.signup_bonus_granted ? 'granted' : 'returned_not_granted',
      {
        signup_bonus_granted: signupResult.signup_bonus_granted,
        grant_status: signupResult.grant_status ?? null,
        http_status: 200,
      }
    );

    isNewUser = signupResult.signup_bonus_granted === true;

    // [Mitigation B] Mark recovery done so AuthContext's recovery effect
    // skips re-firing signupUser on subsequent mounts for this user.
    markSignupRecoveryDone(session.user.id);

    // Invalidate user + credits queries so downstream hooks pick up the new user/credits
    invalidateAfterSignup(session.user.id);

    if (isNewUser && signupResult.credits) {
      setStatus(`Welcome! You received ${signupResult.credits} free credits!`);
      await new Promise((resolve) => setTimeout(resolve, 1500));
    }
  } catch (signupError) {
    // Distinguish chunk-load failures (rare on this path since signupUser is
    // statically imported above, but possible on cold starts) from API-call
    // failures (signupUser fetch returned non-2xx or network-errored). The
    // wrapping prefix bypasses sentry.ts's ignoreErrors filter for bare
    // network errors, so we get full visibility on the post-verify failure
    // mode that today is invisible (today's catch only writes a PostHog event).
    const errMsg = signupError instanceof Error ? signupError.message : String(signupError);
    const errName = signupError instanceof Error ? signupError.name : 'UnknownError';
    const isChunkLoad =
      errName === 'ChunkLoadError' ||
      errMsg.includes('dynamically imported module') ||
      errMsg.includes('Loading chunk');
    const stage = isChunkLoad ? 'chunk_load' : 'post_verify_signup';

    Sentry.captureException(
      new Error(`[auth-handoff] ${stage} failed: ${errMsg}`),
      {
        tags: {
          stage,
          path: 'auth_callback',
          error_class: errName,
        },
        contexts: {
          auth_handoff: {
            email_domain: email.split('@')[1] ?? null,
          },
        },
      }
    );

    trackAuthEvent(posthog, 'signup_api_failed', {
      error: errMsg,
      error_class: errName,
      is_chunk_load: isChunkLoad,
      stage,
    }, session.user);

    grantOutcome(isChunkLoad ? 'failed_chunk_load' : 'failed_fetch', {
      error_class: errName,
      error_message: errMsg,
    });
    // Continue even if signup fails (user might already exist)
  }

  return isNewUser;
}

/**
 * Track signup or login event based on whether the user is new.
 */
function trackSignupOrLogin(
  posthog: ReturnType<typeof usePostHog>,
  session: Session,
  isNewUser: boolean
): void {
  const provider = session.user.app_metadata?.provider || 'email';
  trackAuthEvent(posthog, isNewUser ? 'signup' : 'login', {
    email: session.user.email,
    method: provider,
    external_id: session.user.id,
    user_id: session.user.id,
    is_new_user: isNewUser,
  }, session.user);
}

/**
 * Track successful callback completion to analytics.
 */
function trackCompletion(
  posthog: ReturnType<typeof usePostHog>,
  context: CallbackContext,
  session: Session,
  isNewUser: boolean
): void {
  const duration = Date.now() - context.startTime;
  trackAuthEvent(posthog, 'oauth_callback_completed', {
    duration_ms: duration,
    is_mobile: context.isMobile,
    is_facebook_browser: context.isFacebookBrowser,
    is_new_user: isNewUser,
    auth_method: 'oauth',
  }, session.user);
}

/**
 * Navigate to the return path, cleaning up browser history.
 */
function redirect(returnPath: string, navigate: NavigateFunction): void {
  let redirectTo = returnPath;
  if (!redirectTo || redirectTo === '/auth/callback') {
    redirectTo = '/';
  }

  window.history.replaceState(null, '', redirectTo);
  navigate(redirectTo, { replace: true });
}

// ============================================================================
// Component
// ============================================================================

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>('Signing you in...');
  const posthog = usePostHog();

  useEffect(() => {
    const handleCallback = async () => {
      const context = getCallbackContext();

      if (context.wasOnCriticalPage) {
        trackRevenueProtection(posthog, context);
      }

      try {
        if (checkForOAuthErrors(navigate)) return;

        const session = await getSessionOrReject(context, navigate);
        if (!session) return;

        const email = await resolveEmail(session, navigate, setStatus);
        if (!email) return;

        const isNewUser = await handleSignup(email, session, posthog, setStatus);
        trackSignupOrLogin(posthog, session, isNewUser);
        trackCompletion(posthog, context, session, isNewUser);
        redirect(context.returnPath, navigate);
        // Clear OAuth keys AFTER redirect so App.tsx UNIFIED GUARD still sees
        // oauthPending=true and doesn't compete with this redirect
        localStorage.removeItem(OAUTH_RETURN_KEY);
        localStorage.removeItem('lander3_oauth_return');
      } catch (error) {
        navigate('/login', { replace: true });
      }
    };

    handleCallback();
  }, [navigate, posthog]);

  return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white text-lg">{status}</p>
      </div>
    </div>
  );
}
